import { useState, useEffect, useCallback } from 'react';
import {
  Search,
  ChevronLeft,
  ChevronRight,
  Mic,
  FileText,
  CheckCircle2,
  XCircle,
  Clock,
  User,
  AlertTriangle,
  X,
} from 'lucide-react';
import { analyticsApi } from '../../services/api';

interface HistoryEntry {
  id: string;
  user_id: string;
  user_name: string;
  user_email: string;
  event_type: string;
  audio_duration_seconds: number | null;
  transcription_text: string | null;
  whisper_language: string | null;
  model_used: string | null;
  ai_response_text: string | null;
  exam_category: string | null;
  latency_ms: number | null;
  status: string;
  error_message: string | null;
  created_at: string;
}

interface HistoryResponse {
  items: HistoryEntry[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
  available_models: string[];
}

export default function UsageHistory({ days }: { days: number }) {
  const [data, setData] = useState<HistoryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [eventFilter, setEventFilter] = useState('');
  const [modelFilter, setModelFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const result = await analyticsApi.history({
        page,
        per_page: 20,
        event_type: eventFilter || undefined,
        model: modelFilter || undefined,
        status: statusFilter || undefined,
        search: search || undefined,
        days,
      });
      setData(result);
    } catch (err) {
      console.error('Failed to fetch history:', err);
    } finally {
      setLoading(false);
    }
  }, [page, eventFilter, modelFilter, statusFilter, search, days]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [eventFilter, modelFilter, statusFilter, search, days]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearch('');
  };

  const formatDate = (iso: string) => {
    return new Date(iso).toLocaleString('en-AU', {
      hour12: false,
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const stripHtml = (html: string) => {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  };

  return (
    <div className="space-y-4">
      {/* Filters Bar */}
      <div className="bg-white dark:bg-surface-900 rounded-lg border border-gray-200 dark:border-surface-800 p-4">
        <div className="flex flex-wrap items-center gap-3">
          {/* Search */}
          <form onSubmit={handleSearch} className="flex-1 min-w-[240px] relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search transcriptions, responses, categories..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full pl-9 pr-8 py-2 text-sm border border-gray-300 dark:border-surface-700 rounded-lg bg-white dark:bg-surface-800 text-gray-900 dark:text-gray-100 placeholder-gray-400"
            />
            {searchInput && (
              <button
                type="button"
                onClick={clearSearch}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X size={14} />
              </button>
            )}
          </form>

          {/* Event Type Filter */}
          <select
            value={eventFilter}
            onChange={(e) => setEventFilter(e.target.value)}
            className="text-sm border border-gray-300 dark:border-surface-700 rounded-lg px-3 py-2 bg-white dark:bg-surface-800 text-gray-700 dark:text-gray-200"
          >
            <option value="">All Events</option>
            <option value="transcription">Transcriptions</option>
            <option value="generation">Generations</option>
          </select>

          {/* Model Filter */}
          <select
            value={modelFilter}
            onChange={(e) => setModelFilter(e.target.value)}
            className="text-sm border border-gray-300 dark:border-surface-700 rounded-lg px-3 py-2 bg-white dark:bg-surface-800 text-gray-700 dark:text-gray-200"
          >
            <option value="">All Models</option>
            {data?.available_models.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="text-sm border border-gray-300 dark:border-surface-700 rounded-lg px-3 py-2 bg-white dark:bg-surface-800 text-gray-700 dark:text-gray-200"
          >
            <option value="">All Status</option>
            <option value="success">Success</option>
            <option value="error">Errors</option>
          </select>
        </div>

        {/* Active filters summary */}
        {(search || eventFilter || modelFilter || statusFilter) && (
          <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
            <span>Showing {data?.total || 0} results</span>
            {search && (
              <span className="bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 px-2 py-0.5 rounded-full">
                search: "{search}"
              </span>
            )}
            <button onClick={() => { setEventFilter(''); setModelFilter(''); setStatusFilter(''); clearSearch(); }}
              className="text-primary-600 hover:underline ml-auto">
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* Results */}
      {loading ? (
        <div className="text-center py-12 text-gray-400">Loading...</div>
      ) : !data || data.items.length === 0 ? (
        <div className="bg-white dark:bg-surface-900 rounded-lg border border-gray-200 dark:border-surface-800 p-12 text-center">
          <p className="text-gray-400">No usage history found for the selected filters.</p>
        </div>
      ) : (
        <>
          {/* History Cards */}
          <div className="space-y-3">
            {data.items.map((entry) => (
              <div
                key={entry.id}
                className="bg-white dark:bg-surface-900 rounded-lg border border-gray-200 dark:border-surface-800 overflow-hidden"
              >
                {/* Card Header */}
                <div
                  className="px-4 py-3 flex items-center gap-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-surface-800/50"
                  onClick={() => setExpandedId(expandedId === entry.id ? null : entry.id)}
                >
                  {/* Event type badge */}
                  {entry.event_type === 'transcription' ? (
                    <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 text-xs font-medium min-w-[110px]">
                      <Mic size={13} />
                      Transcription
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-400 text-xs font-medium min-w-[110px]">
                      <FileText size={13} />
                      Generation
                    </div>
                  )}

                  {/* Status */}
                  {entry.status === 'success' ? (
                    <CheckCircle2 size={15} className="text-green-500 flex-shrink-0" />
                  ) : (
                    <XCircle size={15} className="text-red-500 flex-shrink-0" />
                  )}

                  {/* Preview text */}
                  <span className="text-sm text-gray-700 dark:text-gray-300 truncate flex-1">
                    {entry.transcription_text
                      ? entry.transcription_text.slice(0, 120) + (entry.transcription_text.length > 120 ? '...' : '')
                      : entry.error_message || 'No transcription text'}
                  </span>

                  {/* Metadata pills */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {entry.exam_category && (
                      <span className="text-xs bg-gray-100 dark:bg-surface-800 text-gray-600 dark:text-gray-400 px-2 py-0.5 rounded">
                        {entry.exam_category}
                      </span>
                    )}
                    {entry.model_used && (
                      <span className="text-xs bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 px-2 py-0.5 rounded">
                        {entry.model_used}
                      </span>
                    )}
                    {entry.latency_ms != null && (
                      <span className="text-xs text-gray-400 flex items-center gap-1">
                        <Clock size={11} />
                        {(entry.latency_ms / 1000).toFixed(1)}s
                      </span>
                    )}
                    <span className="text-xs text-gray-400 w-36 text-right">
                      {formatDate(entry.created_at)}
                    </span>
                  </div>
                </div>

                {/* Expanded Detail */}
                {expandedId === entry.id && (
                  <div className="border-t border-gray-100 dark:border-surface-800">
                    {/* Meta bar */}
                    <div className="px-4 py-2 bg-gray-50 dark:bg-surface-800/50 flex flex-wrap items-center gap-4 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <User size={12} />
                        {entry.user_name} ({entry.user_email})
                      </span>
                      {entry.whisper_language && (
                        <span>Language: {entry.whisper_language}</span>
                      )}
                      {entry.audio_duration_seconds != null && (
                        <span>Audio: {entry.audio_duration_seconds.toFixed(1)}s</span>
                      )}
                      {entry.exam_category && (
                        <span>Category: {entry.exam_category}</span>
                      )}
                      {entry.model_used && (
                        <span>Model: {entry.model_used}</span>
                      )}
                      {entry.latency_ms != null && (
                        <span>Latency: {(entry.latency_ms / 1000).toFixed(2)}s</span>
                      )}
                    </div>

                    {/* Content area */}
                    <div className="p-4 space-y-4">
                      {/* Transcription / Voice Prompt */}
                      {entry.transcription_text && (
                        <div>
                          <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5 flex items-center gap-1.5">
                            <Mic size={13} className="text-blue-500" />
                            Voice Prompt (Transcription)
                          </h4>
                          <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-900/30 rounded-lg p-3 text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap leading-relaxed">
                            {entry.transcription_text}
                          </div>
                        </div>
                      )}

                      {/* AI Response */}
                      {entry.ai_response_text && (
                        <div>
                          <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5 flex items-center gap-1.5">
                            <FileText size={13} className="text-teal-500" />
                            AI Response (Generated Report)
                          </h4>
                          <div className="bg-teal-50 dark:bg-teal-900/10 border border-teal-100 dark:border-teal-900/30 rounded-lg p-3 text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap leading-relaxed max-h-96 overflow-y-auto">
                            {stripHtml(entry.ai_response_text)}
                          </div>
                        </div>
                      )}

                      {/* Error */}
                      {entry.error_message && (
                        <div>
                          <h4 className="text-xs font-semibold text-red-500 uppercase tracking-wide mb-1.5 flex items-center gap-1.5">
                            <AlertTriangle size={13} />
                            Error
                          </h4>
                          <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-900/30 rounded-lg p-3 text-sm text-red-700 dark:text-red-300 whitespace-pre-wrap">
                            {entry.error_message}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Pagination */}
          {data.total_pages > 1 && (
            <div className="flex items-center justify-between bg-white dark:bg-surface-900 rounded-lg border border-gray-200 dark:border-surface-800 px-4 py-3">
              <span className="text-sm text-gray-500">
                Showing {(data.page - 1) * data.per_page + 1}–{Math.min(data.page * data.per_page, data.total)} of {data.total} entries
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page <= 1}
                  className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100 dark:hover:bg-surface-800 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronLeft size={18} />
                </button>
                {/* Page numbers */}
                {Array.from({ length: Math.min(7, data.total_pages) }, (_, i) => {
                  let pageNum: number;
                  if (data.total_pages <= 7) {
                    pageNum = i + 1;
                  } else if (page <= 4) {
                    pageNum = i + 1;
                  } else if (page >= data.total_pages - 3) {
                    pageNum = data.total_pages - 6 + i;
                  } else {
                    pageNum = page - 3 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      className={`w-8 h-8 rounded-md text-sm font-medium ${
                        pageNum === page
                          ? 'bg-primary-600 text-white'
                          : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-surface-800'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                <button
                  onClick={() => setPage(Math.min(data.total_pages, page + 1))}
                  disabled={page >= data.total_pages}
                  className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100 dark:hover:bg-surface-800 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
